// BlessingBoard.tsx
//
// The one grid on the sheet. Rows are paladins, columns are the classes that
// actually signed, and a cell is what that paladin holds on that class for the
// next 30 minutes. Everything about the layout is trying to make one question
// answerable at a glance: scan DOWN a column to see what your class carries,
// scan ACROSS a row to see your own job as a paladin.
//
// The second table underneath is not a footnote. A greater blessing hits a
// class and a class is not a role, so every mixed column (tank warriors next
// to DPS warriors, holy paladins next to ret) needs a single blessing to
// resolve it, and those are the rows that actually get forgotten mid-raid.

import React, { useState } from "react";
import { getWowClassColor as classColor } from "../wowClassColor";
import { Tip } from "../Floating";
import { Raider } from "./assignmentModel";
import {
  BlessingKey, BlessingPlan, cellKey, colorOf, shortOf,
} from "./blessings";

import { tc, optionStyle } from "../theme/legible";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--c-ink-4)]";

function Chip({ plan, blessing, risky, dim }: {
  plan: BlessingPlan; blessing: BlessingKey; risky?: boolean; dim?: boolean;
}) {
  const m = plan.catalogue.byKey[blessing];
  if (!m) return null;
  return (
    <span className="inline-flex items-center gap-1 truncate text-[11px] font-semibold"
          style={{ color: tc(m.color), opacity: dim ? 0.55 : 1 }}>
      {!m.greater && <span className="text-[8px] font-bold text-[var(--c-ink-4)]">10m</span>}
      {m.short}
      {risky && <span className="text-[9px] text-amber-700 dark:text-amber-300" title="Patched below">*</span>}
    </span>
  );
}

export default function BlessingBoard({
  plan, roster, canManage, touched,
  onSetCell, onSetSingle, onToggleCap, onReset,
}: {
  plan: BlessingPlan;
  roster: Raider[];
  canManage: boolean;
  touched: boolean;
  onSetCell: (paladinId: string, className: string, value: string) => void;
  onSetSingle: (paladinId: string, characterId: string, value: string) => void;
  onToggleCap: (paladinId: string, blessing: BlessingKey, on: boolean) => void;
  onReset: () => void;
}) {
  const [showCaps, setShowCaps] = useState(false);
  const [adding, setAdding] = useState(false);

  // Alliance-only mechanic, and a raid with no paladin should not be told
  // about a grid it can never fill.
  if (plan.paladins.length === 0) return null;

  const P = plan.paladins.length;
  const cat = plan.catalogue;

  return (
    <div data-testid="blessing-board"
         className="mb-3 overflow-hidden rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)]">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--c-line)] px-2.5 py-1.5"
           style={{ background: "linear-gradient(90deg, #c084fc1F, transparent 70%)" }}>
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#c084fc" }} />
        <span className="text-[10px] font-bold uppercase tracking-[0.14em]"
              style={{ color: tc("#c084fc") }}>
          Greater Blessings
        </span>
        <Tip tip={<>A greater blessing lasts <b>30 minutes</b> and hits a whole class.
                   Each paladin holds one per class, so {P} paladin{P > 1 ? "s" : ""} means
                   every class carries {P} of the {cat.greater.length}.</>}>
          <span className="text-[10px] text-[var(--c-ink-4)]">
            30 min · {P} paladin{P > 1 ? "s" : ""} · {P} of {cat.greater.length} per class
          </span>
        </Tip>
        <span className="ml-auto flex items-center gap-2">
          {canManage && (
            <button onClick={() => setShowCaps(!showCaps)}
                    className="text-[10px] uppercase tracking-wider text-[var(--c-ink-4)] hover:text-[var(--c-ink)]">
              {showCaps ? "Hide talents" : "Talents"}
            </button>
          )}
          {canManage && touched && (
            <button onClick={onReset}
                    className="text-[10px] uppercase tracking-wider text-[var(--c-ink-4)] hover:text-[var(--c-ink)]">
              Reset
            </button>
          )}
        </span>
      </div>

      {/* Talent gates. Sanctuary is genuinely Protection-only and Kings costs a
          ret paladin real damage, so the defaults are a guess from spec and the
          lead needs a way to say otherwise without hunting for a settings page. */}
      {showCaps && canManage && (
        <div data-testid="blessing-caps" className="border-b border-[var(--c-line)] bg-[var(--c-surface)] px-2.5 py-2">
          <div className={`${LABEL} mb-1.5`}>
            What each paladin can actually cast · guessed from spec
          </div>
          {plan.paladins.map((p) => (
            <div key={p.raider.characterId} className="mb-1 flex flex-wrap items-center gap-1.5">
              <span className="w-[110px] shrink-0 truncate text-[12px] font-semibold"
                    style={{ color: tc(classColor("Paladin")) }}>
                {p.raider.name}
              </span>
              <span className="w-[64px] shrink-0 text-[10px] text-[var(--c-ink-4)]">{p.spec}</span>
              {cat.all.map((b) => (
                <button key={b}
                        data-testid={`cap-${p.raider.characterId}-${b}`}
                        onClick={() => onToggleCap(p.raider.characterId, b, !p.castable[b])}
                        className={`rounded border px-1.5 py-[2px] text-[10px] font-semibold transition-colors
                                    ${p.castable[b]
                                      ? "border-transparent"
                                      : "border-[var(--c-line)] text-[var(--c-ink-4)] line-through"}`}
                        style={p.castable[b]
                          ? { color: colorOf(cat, b), background: `${colorOf(cat, b)}1A` }
                          : undefined}>
                  {shortOf(cat, b)}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* The grid. Scrolls sideways rather than wrapping: a wrapped matrix is
          not a matrix, and ten classes will not fit a phone either way. */}
      <div className="overflow-x-auto [scrollbar-width:thin]">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className={`${LABEL} sticky left-0 z-10 bg-[var(--c-surface)] px-2.5 py-1 font-semibold`}>
                Paladin
              </th>
              {plan.columns.map((c) => (
                <th key={c.className} className="px-1.5 py-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-[11px] font-bold" style={{ color: tc(classColor(c.className)) }}>
                      {c.className}
                    </span>
                    <span className="text-[9px] text-[var(--c-ink-4)]">{c.members.length}</span>
                    {c.tanks.length > 0 && (
                      <Tip tip={<>{c.tanks.map((t) => t.name).join(", ")} tank{c.tanks.length > 1 ? "" : "s"} out
                                 of this column, so it can never take Salvation as a group.</>}>
                        <span className="text-[9px] font-bold" style={{ color: tc("#38bdf8") }}>
                          {c.tanks.length}T
                        </span>
                      </Tip>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plan.paladins.map((p) => (
              <tr key={p.raider.characterId} className="border-t border-[var(--c-line)]">
                <td className="sticky left-0 z-10 bg-[var(--c-surface)] px-2.5 py-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="truncate text-[12px] font-semibold"
                          style={{ color: tc(classColor("Paladin")) }}>
                      {p.raider.name}
                    </span>
                    {p.lane && (
                      <Tip tip={<>Runs the <b>{shortOf(cat, p.lane)}</b> lane. One blessing per
                                 paladin is what survives a re-buff after a wipe.</>}>
                        <span className="text-[9px] uppercase tracking-wider"
                              style={{ color: tc(colorOf(cat, p.lane)), opacity: 0.7 }}>
                          {shortOf(cat, p.lane)}
                        </span>
                      </Tip>
                    )}
                  </div>
                </td>
                {plan.columns.map((c) => {
                  const cell = plan.grid[cellKey(p.raider.characterId, c.className)];
                  const id = `cell-${p.raider.characterId}-${c.className}`;
                  if (!canManage) {
                    return (
                      <td key={c.className} data-testid={id} className="px-1.5 py-1">
                        {cell
                          ? <Chip plan={plan} blessing={cell.blessing} risky={cell.risky}
                                  dim={cell.source === "auto"} />
                          : <span className="text-[11px] text-[var(--c-ink-4)]">·</span>}
                      </td>
                    );
                  }
                  return (
                    <td key={c.className} data-testid={id} className="px-1 py-[2px]">
                      <select
                        value={cell ? cell.blessing : ""}
                        onChange={(e) => onSetCell(p.raider.characterId, c.className, e.target.value)}
                        className={`w-full min-w-[78px] rounded border bg-transparent px-1 py-[2px]
                                    text-[11px] font-semibold outline-none focus:border-amber-400/60
                                    ${cell && cell.source === "manual"
                                      ? "border-amber-400/30" : "border-transparent hover:border-[var(--c-line)]"}`}
                        style={{ color: cell ? tc(colorOf(cat, cell.blessing)) : "var(--c-ink-4)" }}
                      >
                        <option value="" style={optionStyle()}>·</option>
                        {cat.all.map((b) => (
                          <option key={b} value={b} style={{ ...optionStyle(), color: tc(colorOf(cat, b)) }}
                                  disabled={!p.castable[b]}>
                            {shortOf(cat, b)}
                            {!cat.byKey[b].greater ? " (10m)" : ""}
                            {c.tanks.length > 0 && b === "salvation" ? " *" : ""}
                          </option>
                        ))}
                      </select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Single blessings: the fix for every column that holds two different
          jobs. Each of these overwrites that paladin's greater blessing on that
          one person, and only for 10 minutes, which is the whole reason the row
          has to be written down. */}
      {(plan.singles.length > 0 || (canManage && adding)) && (
        <div data-testid="blessing-singles" className="border-t border-[var(--c-line)] bg-[var(--c-surface)] px-2.5 py-2">
          <div className="mb-1.5 flex items-center gap-2">
            <span className={LABEL}>Single blessings · 10 min · recast every wipe</span>
            <Tip tip={<>A single blessing <b>replaces that paladin's own</b> greater blessing on that
                       person. Other paladins are unaffected, which is how a tank keeps Kings and
                       Sanctuary while the DPS in their class keep Salvation.</>}>
              <span className="text-[9px] text-[var(--c-ink-4)]">why</span>
            </Tip>
            {canManage && (
              <button onClick={() => setAdding(!adding)}
                      className="ml-auto text-[10px] uppercase tracking-wider text-[var(--c-ink-4)] hover:text-amber-700 dark:hover:text-amber-300">
                {adding ? "Done" : "+ Add"}
              </button>
            )}
          </div>

          {plan.singles.map((s) => (
            <div key={s.key} data-testid={`single-${s.key}`}
                 className="mb-[3px] flex flex-wrap items-center gap-1.5">
              <span className="w-[110px] shrink-0 truncate text-[12px] font-semibold"
                    style={{ color: tc(classColor("Paladin")) }}>
                {s.paladinName}
              </span>
              <span className="text-[10px] text-[var(--c-ink-4)]">→</span>
              <span className="w-[110px] shrink-0 truncate text-[12px] font-semibold"
                    style={{ color: tc(classColor(s.raider.className)) }}>
                {s.raider.name}
              </span>
              {canManage ? (
                <select
                  value={s.blessing}
                  onChange={(e) => onSetSingle(s.paladinId, s.raider.characterId, e.target.value)}
                  className="w-[92px] shrink-0 rounded border border-transparent bg-transparent px-1
                             py-[2px] text-[11px] font-semibold outline-none hover:border-[var(--c-line)]
                             focus:border-amber-400/60"
                  style={{ color: tc(colorOf(cat, s.blessing)) }}
                >
                  <option value="" style={optionStyle()}>Remove</option>
                  {cat.all.map((b) => (
                    <option key={b} value={b} style={{ ...optionStyle(), color: tc(colorOf(cat, b)) }}>
                      {shortOf(cat, b)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="w-[92px] shrink-0"><Chip plan={plan} blessing={s.blessing} /></span>
              )}
              <span className="truncate text-[10px] text-[var(--c-ink-4)]">{s.reason}</span>
            </div>
          ))}

          {canManage && adding && (
            <AddSingle plan={plan} roster={roster} onAdd={onSetSingle} />
          )}
        </div>
      )}

      {/* Gaps are reported, not fixed. Past the patches above, every further
          swap costs somebody a blessing they do want. */}
      {plan.gaps.length > 0 && (
        <div data-testid="blessing-gaps"
             className="border-t border-[var(--c-line)] px-2.5 py-1.5 text-[10px] text-[var(--c-ink-4)]">
          <span className={LABEL}>Not covered</span>{" "}
          {plan.gaps.slice(0, 8).map((g, i) => (
            <span key={`${g.raider.characterId}-${g.blessing}`}>
              {i > 0 && <span className="text-[var(--c-ink-4)]"> · </span>}
              <span style={{ color: tc(classColor(g.raider.className)) }}>{g.raider.name}</span>
              <span className="text-[var(--c-ink-4)]"> no </span>
              <span style={{ color: tc(colorOf(cat, g.blessing)) }}>
                {shortOf(cat, g.blessing)}
              </span>
            </span>
          ))}
          {plan.gaps.length > 8 && (
            <span className="text-[var(--c-ink-4)]"> +{plan.gaps.length - 8} more</span>
          )}
        </div>
      )}

      {plan.warnings.length > 0 && (
        <div data-testid="blessing-warnings" className="border-t border-[var(--c-line)] px-2.5 py-1.5">
          {plan.warnings.map((w) => (
            <div key={w.key} className="flex items-start gap-1.5 py-[1px] text-[10px]">
              <span className={`mt-[4px] h-1 w-1 shrink-0 rounded-full
                                ${w.tone === "warn" ? "bg-amber-400" : "bg-[var(--c-sunken-2)]"}`} />
              <span className={w.tone === "warn" ? "text-amber-700/80 dark:text-amber-300/80" : "text-[var(--c-ink-4)]"}>
                {w.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Adding a single by hand: paladin, target, blessing. Kept as three plain
// selects because it is used twice a night at most.
function AddSingle({ plan, roster, onAdd }: {
  plan: BlessingPlan;
  roster: Raider[];
  onAdd: (paladinId: string, characterId: string, value: string) => void;
}) {
  const [pal, setPal] = useState("");
  const [target, setTarget] = useState("");
  const [blessing, setBlessing] = useState<string>("");
  const cat = plan.catalogue;
  const palInfo = plan.paladins.filter((p) => p.raider.characterId === pal)[0] || null;

  return (
    <div data-testid="add-single" className="mt-1.5 flex flex-wrap items-center gap-1.5
                                             border-t border-[var(--c-line)] pt-1.5">
      <select value={pal} onChange={(e) => setPal(e.target.value)}
              className="w-[120px] rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1 py-[2px]
                         text-[11px] text-[var(--c-ink-2)] outline-none focus:border-amber-400/60">
        <option value="" style={optionStyle()}>Paladin</option>
        {plan.paladins.map((p) => (
          <option key={p.raider.characterId} value={p.raider.characterId} style={optionStyle()}>
            {p.raider.name}
          </option>
        ))}
      </select>
      <select value={target} onChange={(e) => setTarget(e.target.value)}
              className="w-[130px] rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1 py-[2px]
                         text-[11px] text-[var(--c-ink-2)] outline-none focus:border-amber-400/60">
        <option value="" style={optionStyle()}>Onto</option>
        {roster.filter((r) => !r.isBench).map((r) => (
          <option key={r.characterId} value={r.characterId}
                  style={{ ...optionStyle(), color: tc(classColor(r.className)) }}>
            {r.name}
          </option>
        ))}
      </select>
      <select value={blessing} onChange={(e) => setBlessing(e.target.value)}
              className="w-[100px] rounded border border-[var(--c-line)] bg-[var(--c-surface)] px-1 py-[2px]
                         text-[11px] outline-none focus:border-amber-400/60"
              style={{ color: tc(colorOf(cat, blessing)) }}>
        {cat.all.map((b) => (
          <option key={b} value={b} style={{ ...optionStyle(), color: tc(colorOf(cat, b)) }}
                  disabled={!!palInfo && !palInfo.castable[b]}>
            {shortOf(cat, b)}
          </option>
        ))}
      </select>
      <button
        disabled={!pal || !target || !blessing}
        onClick={() => { onAdd(pal, target, blessing); setTarget(""); }}
        className="rounded bg-[var(--c-accent)] px-2 py-[2px] text-[10px] font-bold uppercase
                   tracking-wider text-[var(--c-ink-on-fill)] disabled:opacity-25"
      >
        Add
      </button>
    </div>
  );
}
